import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { getToken } from 'firebase/messaging';

import * as MerchantService from '../../api/merchantService';
import * as StampCardService from '../../api/stampCardService';
import * as NotificationService from '../../api/notificationService';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';

import useInitContext from '../../hooks/useInitContext';
import useDeepLinkContext from '../../hooks/useDeepLinkContext';
import useDeepLinkHandler from '../../hooks/useDeepLinkHandler';
import useStampCardContext from '../../hooks/useStampCardContext';
import useMerchantContext from '../../hooks/useMerchantContext';
import { messaging } from '../../lib/authHook/firebase';
import SplashLocation from '../../assets/images/splash.jpeg';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundImage: `url(${SplashLocation})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    height: '100vh',
    width: '100vw',
  },
  grow: {
    flexGrow: 1,
  },
  noStampTitle: {
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(),
    textAlign: 'center',
  },
  noStampText: {
    marginTop: theme.spacing(),
    textAlign: 'center',
  },
  dialogTextContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    marginLeft: theme.spacing(),
  },
  homeContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: theme.spacing(),
  },
  homeIcon: {
    marginRight: theme.spacing(1),
    fontSize: 30,
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
  subTitle: {
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    paddingLeft: theme.spacing(4),
  },
  toolbar: {
    height: 40,
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(3),
  },
  textContainer: {
    background: 'rgba(255,255,255,0.8)',
    borderRadius: '24px',
    padding: '32px 48px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '75%',
  },
}));

/**
 * Root redirects to the user's companies dashboard.
 */
export default function Root() {
  const classes = useStyles();
  const location = useLocation();
  const params = queryString.parse(location.search);
  const feature = params['feature'];
  const merchantId = params['merchant'];
  const contactlessCode = params['code'];
  const couponCode = params['code'];
  const merchantCode = params['merchant'];
  const userCode = params['user'];
  const singleUseCode = params['code'];
  const store = params['store'];
  const stampNum = params['numOfStamps'];
  const [isLoading, setIsLoading] = useState(true);
  const dl = useDeepLinkContext();
  const init = useInitContext();
  const selectedStampCard = useStampCardContext();
  const selectedMerchant = useMerchantContext();
  const initialized = useRef(false);
  const [merchantList, setMerchantList] = useState<MerchantService.Merchant[]>(
    [],
  );
  const [requestToken, setRequestToken] = useState(true);
  const VAPID_PUBLIC_KEY = process.env.REACT_APP_FIREBASE_VAP_ID as string;
  const history = useHistory();

  const requestPermission = useCallback(async () => {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      // Wait for Service Worker to be ready before getting token
      if ('serviceWorker' in navigator) {
        try {
          await navigator.serviceWorker.ready;
          const token = await getToken(messaging, {
            vapidKey: VAPID_PUBLIC_KEY,
          });
          console.log('Token Gen', token);
          if (token && token.length > 0) {
            await NotificationService.register(token, 'browser');
            setRequestToken(false);
          }
        } catch (e) {
          console.log('Error:', e);
          setRequestToken(false);
        }
      }
    } else if (permission === 'denied') {
      console.log('Denied for the notification');
      setRequestToken(false);
    }
  }, [VAPID_PUBLIC_KEY]);

  useEffect(() => {
    if (requestToken) {
      requestPermission();
    }
  }, [requestToken, requestPermission]);

  useEffect(() => {
    const setDeeplink = () => {
      if (dl && isLoading && !initialized.current) {
        initialized.current = true;
        dl.setDeeplink({
          feature: feature && typeof feature == 'string' ? feature : undefined,
          merchantId:
            merchantId && typeof merchantId == 'string'
              ? merchantId
              : undefined,
          contactlessCode:
            contactlessCode && typeof contactlessCode == 'string'
              ? contactlessCode
              : undefined,
          couponCode:
            couponCode && typeof couponCode == 'string'
              ? couponCode
              : undefined,
          merchantCode:
            merchantCode && typeof merchantCode == 'string'
              ? merchantCode
              : undefined,
          userCode:
            userCode && typeof userCode == 'string' ? userCode : undefined,
          singleUseCode:
            singleUseCode && typeof singleUseCode == 'string'
              ? singleUseCode
              : undefined,
          store: store && typeof store == 'string' ? store : undefined,
          stampNum:
            stampNum && typeof stampNum == 'string' ? stampNum : undefined,
        });
      }
    };

    if (isLoading) {
      setDeeplink();
    }
  }, [
    isLoading,
    feature,
    merchantId,
    contactlessCode,
    couponCode,
    merchantCode,
    userCode,
    dl,
    singleUseCode,
    store,
    stampNum,
  ]);

  useEffect(() => {
    const setDeeplink = () => {
      if (dl && isLoading && !initialized.current) {
        initialized.current = true;
        dl.setDeeplink({
          feature: feature && typeof feature == 'string' ? feature : undefined,
          merchantId:
            merchantId && typeof merchantId == 'string'
              ? merchantId
              : undefined,
          contactlessCode:
            contactlessCode && typeof contactlessCode == 'string'
              ? contactlessCode
              : undefined,
          couponCode:
            couponCode && typeof couponCode == 'string'
              ? couponCode
              : undefined,
          merchantCode:
            merchantCode && typeof merchantCode == 'string'
              ? merchantCode
              : undefined,
          userCode:
            userCode && typeof userCode == 'string' ? userCode : undefined,
          singleUseCode:
            singleUseCode && typeof singleUseCode == 'string'
              ? singleUseCode
              : undefined,
          store: store && typeof store == 'string' ? store : undefined,
          stampNum:
            stampNum && typeof stampNum == 'string' ? stampNum : undefined,
        });
      }
      fetch();
    };

    const fetch = async () => {
      try {
        const resMerchants = await MerchantService.listMerchantsByMembership();
        setMerchantList(resMerchants);
        const resList = await StampCardService.list();

        if (init?.init?.isFirstLoginSignup) {
          init.setInit({ ...init.init, isFirstLoginSignup: false });
          if (resMerchants.length > 0 && !feature) {
            let firstStampCard = resList.find(
              (card) => card.merchantID === resMerchants[0].id,
            );
            if (firstStampCard) {
              selectedStampCard?.setStampCard({
                selectedStampCardId: firstStampCard.stampCards[0].id,
                selectedSlotNumber: firstStampCard.stampCards[0].slotNumber,
                selectedStampCampaignId:
                  firstStampCard.stampCards[0].loyalty_campaign.ID,
              });
              selectedMerchant?.setMerchant({ merchantId: resMerchants[0].id });
            }
            history.push(`/merchant`);
            setIsLoading(false);
          } else {
            // Handle deeplink after first login signup
            if (
              selectedMerchant &&
              selectedStampCard &&
              selectedMerchant.merchant &&
              selectedMerchant.merchant.merchantId &&
              selectedStampCard.stampcard &&
              selectedStampCard.stampcard.selectedStampCardId
            ) {
              console.log('First login with deeplink');
              history.push(`/merchant`);
              setIsLoading(false);
            }
          }
        } else {
          if (
            selectedMerchant &&
            selectedStampCard &&
            selectedMerchant.merchant &&
            selectedMerchant.merchant.merchantId &&
            selectedStampCard.stampcard &&
            selectedStampCard.stampcard.selectedStampCardId
          ) {
            console.log('Root fetch else');
            history.push(`/merchant`);
            setIsLoading(false);
          } else {
            console.log('No selected merchant or stamp card');
            setIsLoading(false);
          }
        }
      } catch (e) {
        console.log('Error fetching merchant or stamp card', e);
        setIsLoading(false);
      }
    };

    // setDevice(detectDevice);
    if (isLoading) {
      setDeeplink();
    }
  }, [
    history,
    isLoading,
    init,
    selectedMerchant,
    selectedStampCard,
    feature,
    merchantId,
    contactlessCode,
    couponCode,
    merchantCode,
    userCode,
    dl,
    singleUseCode,
    store,
    stampNum,
  ]);

  useDeepLinkHandler();

  if (isLoading) {
    return null;
  }

  return (
    <>
      {(!merchantList || merchantList.length === 0) && (
        <div className={classes.container}>
          <div className={classes.textContainer}>
            <Typography variant="h5" className={classes.noStampTitle}>
              No Programs Found
            </Typography>
            <Typography variant="body1" className={classes.noStampText}>
              Visit a participating merchant and scan a QR code to get started
            </Typography>
          </div>
        </div>
      )}
    </>
  );
}
